import React, { useState } from 'react';
import { ChevronRight, Phone, Mail, MapPin, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import WhatsAppButton from '../components/WhatsAppButton';
import mockData from '../mock';

const Home = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    mensaje: ''
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setFormStatus({
          type: 'success',
          message: data.message
        });
        // Reset form
        setFormData({
          nombre: '',
          apellido: '',
          email: '',
          telefono: '',
          mensaje: ''
        });
      } else {
        setFormStatus({
          type: 'error',
          message: 'Hubo un error al enviar tu mensaje. Por favor intentá nuevamente.'
        });
      }
    } catch (error) {
      setFormStatus({
        type: 'error',
        message: 'Error de conexión. Por favor verificá tu conexión a internet.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="/assets/logo-ilposto-new.png" 
                alt="IL POSTO Logo" 
                className="h-12 w-auto"
              />
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#inicio" className="text-gray-700 hover:text-red-600 transition-colors font-medium">Inicio</a>
              <a href="#nosotros" className="text-gray-700 hover:text-red-600 transition-colors font-medium">Nosotros</a>
              <a href="#productos" className="text-gray-700 hover:text-red-600 transition-colors font-medium">Productos</a>
              <a href="#clientes" className="text-gray-700 hover:text-red-600 transition-colors font-medium">Clientes</a>
              <a href="#contacto" className="text-gray-700 hover:text-red-600 transition-colors font-medium">Contacto</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="inicio" className="relative h-screen flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-red-600/20 via-transparent to-black/40 z-10"></div>
          <img
            src={mockData.heroImage}
            alt="IL POSTO Furniture"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative z-20 container mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Donde el <span className="text-red-500">arte</span><br />se une con la <span className="text-red-500">funcionalidad</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto">
            Creamos mobiliario excepcional que transforma espacios gastronómicos en experiencias inolvidables
          </p>
          <a 
            href="/assets/catalogo-ilposto.pdf" 
            download="Catalogo_IL_POSTO.pdf"
            className="inline-block"
          >
            <Button 
              size="lg" 
              className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg rounded-full shadow-2xl hover:shadow-red-500/50 transition-all duration-300 transform hover:scale-105"
            >
              Descargá nuestro catálogo
              <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="nosotros" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block mb-6">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center">
                  <div className="w-12 h-12 border-2 border-white rounded-full"></div>
                </div>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Más de <span className="text-red-600">50 años</span> transformando la industria
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {mockData.aboutText}
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Desde nuestra fábrica en Buenos Aires, combinamos la maestría artesanal heredada con innovación tecnológica de vanguardia para ofrecerte soluciones que superan las expectativas más exigentes.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="border-l-4 border-red-600 pl-4">
                  <div className="text-3xl font-bold text-gray-900">+50</div>
                  <div className="text-sm text-gray-600">Años de trayectoria</div>
                </div>
                <div className="border-l-4 border-red-600 pl-4">
                  <div className="text-3xl font-bold text-gray-900">+5000</div>
                  <div className="text-sm text-gray-600">Clientes satisfechos</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/assets/showroom-ilposto-rotated.jpg"
                  alt="IL POSTO Showroom"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-full h-full bg-red-600/10 rounded-2xl -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Manufacturing Process Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Nuestro <span className="text-red-600">Proceso</span> Productivo
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Calidad artesanal con tecnología de vanguardia
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="relative group overflow-hidden rounded-2xl shadow-xl">
              <img
                src="/assets/fabrica-2.jpg"
                alt="Proceso de Fabricación IL POSTO"
                className="w-full h-96 object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent">
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-bold text-white mb-2">Maestría Artesanal</h3>
                  <p className="text-white/90">
                    Cada pieza pasa por las manos expertas de nuestros artesanos
                  </p>
                </div>
              </div>
            </div>

            <div className="relative group overflow-hidden rounded-2xl shadow-xl">
              <img
                src="/assets/materia-prima.jpg"
                alt="Materia Prima IL POSTO"
                className="w-full h-96 object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent">
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-bold text-white mb-2">Materia Prima</h3>
                  <p className="text-white/90">
                    Nos aseguramos de elegir la mejor madera para garantizar durabilidad en tus diseños
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="productos" className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Nuestras <span className="text-red-600">Líneas</span> de Productos
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Diseño y resistencia pensados para soportar el exigente ritmo de la gastronomía profesional
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mockData.productCategories.map((category, index) => (
              <Card 
                key={index}
                className="group cursor-pointer overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
                onClick={() => setSelectedCategory(category)}
              >
                <div className="relative h-80 overflow-hidden">
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-2xl font-bold text-white">{category.name}</h3>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Client Gallery Section */}
      <section id="clientes" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block mb-6">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center">
                <div className="w-12 h-12 border-2 border-white rounded-full"></div>
              </div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Nuestros clientes nos respaldan
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Descubrí cómo nuestras soluciones transforman restaurantes, bares y hoteles de todo el país
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockData.clientGallery.map((project, index) => (
              <div 
                key={index}
                className="group relative h-96 overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500"
              >
                <img
                  src={project.image}
                  alt={project.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-2xl font-bold text-white mb-2">{project.name}</h3>
                    {project.location && <p className="text-white/80">{project.location}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="py-24 bg-gray-100">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
                ¿Querés darle un nuevo estilo a tu local?
              </h2>
              <p className="text-xl text-gray-700 mb-8">
                Conectate con nosotros y encontrá la solución perfecta de mobiliario para tu proyecto gastronómico u hotelero. Estamos para ayudarte.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-lg text-gray-900">Dirección</div>
                    <div className="text-gray-700">{mockData.contact.address}</div>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-lg text-gray-900">Teléfono</div>
                    <div className="text-gray-700">11 4889-3734</div>
                    <div className="text-gray-700">11 5976-9090</div>
                    <div className="text-gray-700">{mockData.contact.phone}</div>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-lg text-gray-900">Email</div>
                    <div className="text-gray-700">{mockData.contact.email}</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg">
              <h3 className="text-2xl font-bold mb-6 text-gray-900">Envianos tu consulta</h3>
              
              {formStatus.message && (
                <div className={`mb-4 p-4 rounded-lg ${
                  formStatus.type === 'success' 
                    ? 'bg-green-100 border border-green-300 text-green-800' 
                    : 'bg-red-100 border border-red-300 text-red-800'
                }`}>
                  {formStatus.message}
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    placeholder="Nombre"
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:border-red-500 transition-colors text-gray-900 placeholder-gray-500"
                  />
                  <input
                    type="text"
                    name="apellido"
                    value={formData.apellido}
                    onChange={handleInputChange}
                    placeholder="Apellido"
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:border-red-500 transition-colors text-gray-900 placeholder-gray-500"
                  />
                </div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Email"
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:border-red-500 transition-colors text-gray-900 placeholder-gray-500"
                />
                <input
                  type="tel"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleInputChange}
                  placeholder="Teléfono"
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:border-red-500 transition-colors text-gray-900 placeholder-gray-500"
                />
                <textarea
                  name="mensaje"
                  value={formData.mensaje}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Escribí tu mensaje"
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:border-red-500 transition-colors resize-none text-gray-900 placeholder-gray-500"
                ></textarea>
                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg rounded-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar consulta'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="/assets/logo-ilposto-new.png" 
                  alt="IL POSTO Logo" 
                  className="h-10 w-auto"
                />
              </div>
              <p className="text-gray-400">
                Sillas & Mesas | Hotelería y Gastronomía
              </p>
            </div>
            
            <div>
              <h3 className="font-bold text-lg mb-4">Navegación</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#inicio" className="hover:text-red-500 transition-colors">Inicio</a></li>
                <li><a href="#nosotros" className="hover:text-red-500 transition-colors">Nosotros</a></li>
                <li><a href="#productos" className="hover:text-red-500 transition-colors">Productos</a></li>
                <li><a href="#clientes" className="hover:text-red-500 transition-colors">Clientes</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-bold text-lg mb-4">Seguinos</h3>
              <div className="flex space-x-4">
                <a href="https://www.instagram.com/sillas.ilposto/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors">
                  <Instagram className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2025 IL POSTO. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <WhatsAppButton />
    </div>
  );
};

export default Home;